import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit as fbLimit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  where,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';
import { env } from '@/config/env';
import { auth } from '@/lib/firebase';
import type { Order, OrderItem, OrderStatus } from '@/types/order';
import type { Owner } from '@/types/owner';

export type CreateOrderInput = {
  ownerId: string;
  shopName: string;
  customerName: string;
  customerPhone: string;
  customerWhatsapp?: string;
  deliveryAddress: string;
  notes?: string;
  items: OrderItem[];
  estimatedTotal: number;
  currency?: string;
};

async function postFunction(path: string, body: unknown): Promise<void> {
  try {
    await fetch(`${env.functionsBaseUrl}/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (err) {
    console.warn(`Failed to call ${path}`, err);
  }
}

export async function createOrder(input: CreateOrderInput): Promise<string> {
  const now = Date.now();
  const doc = await addDoc(collection(db, 'orders'), {
    ...input,
    status: 'new' as OrderStatus,
    createdAt: now,
    updatedAt: now,
  });
  void postFunction('sendNewOrderNotification', { orderId: doc.id });
  return doc.id;
}

export function subscribeToOrder(
  orderId: string,
  cb: (order: Order | null) => void,
): () => void {
  return onSnapshot(
    doc(db, 'orders', orderId),
    (snap) => {
      cb(snap.exists() ? { orderId: snap.id, ...(snap.data() as Omit<Order, 'orderId'>) } : null);
    },
    (err) => {
      console.warn('Order snapshot error', err);
      cb(null);
    },
  );
}

export function subscribeOwnerOrders(
  ownerId: string,
  cb: (orders: Order[]) => void,
  onError?: (err: unknown) => void,
): () => void {
  const q = query(collection(db, 'orders'), where('ownerId', '==', ownerId));
  return onSnapshot(
    q,
    (snap) => {
      const list = snap.docs.map((d) => ({ ...(d.data() as Order), orderId: d.id }));
      list.sort((a, b) => b.createdAt - a.createdAt);
      cb(list);
    },
    (err) => {
      console.warn('Orders snapshot error', err);
      onError?.(err);
    },
  );
}

export async function listOwnerOrders(ownerId: string, max = 100): Promise<Order[]> {
  const q = query(
    collection(db, 'orders'),
    where('ownerId', '==', ownerId),
    fbLimit(max),
  );
  const snap = await getDocs(q);
  const list = snap.docs.map((d) => ({ ...(d.data() as Order), orderId: d.id }));
  list.sort((a, b) => b.createdAt - a.createdAt);
  return list;
}

export async function updateOrderStatus(
  orderId: string,
  status: OrderStatus,
): Promise<void> {
  await setDoc(
    doc(db, 'orders', orderId),
    { status, updatedAt: Date.now() },
    { merge: true },
  );
  void postFunction('sendOrderStatusNotification', { orderId });
}

export async function cancelOrder(orderId: string): Promise<void> {
  await updateOrderStatus(orderId, 'cancelled');
}

export async function findShopBySlug(slug: string): Promise<Owner | null> {
  const q = query(collection(db, 'owners'), where('shopSlug', '==', slug), fbLimit(1));
  const snap = await getDocs(q);
  const first = snap.docs[0];
  return first ? (first.data() as Owner) : null;
}

export async function ensureSignedIn(): Promise<void> {
  if (!auth.currentUser) throw new Error('You must be logged in.');
}

export {
  orderBy as _orderBy, // re-export to keep tree-shaking happy if needed
};
